export class ClusterBuildingTimelineDragWidget {
  private root: HTMLElement;
  private desktopMq = window.matchMedia('(min-width: 992px)');
  private dragging = false;
  private startX = 0;
  private startScroll = 0;
  private observer: IntersectionObserver | null = null;
  private hasAutoScrolled = false;

  constructor(root: HTMLElement) {
    this.root = root;
  }

  init(): void {
    this.destroy();
    this.sync();

    this.initAutoScroll();

    this.root.addEventListener('pointerdown', this.onDown);
    this.root.addEventListener('pointermove', this.onMove);
    this.root.addEventListener('pointerup', this.onUp);
    this.root.addEventListener('pointercancel', this.onUp);
    this.desktopMq.addEventListener('change', this.sync);
    window.addEventListener('resize', this.sync);
  }

  destroy(): void {
    this.dragging = false;
    this.root.classList.remove('is-dragging', 'is-scrollable');
    this.root.removeEventListener('pointerdown', this.onDown);
    this.root.removeEventListener('pointermove', this.onMove);
    this.root.removeEventListener('pointerup', this.onUp);
    this.root.removeEventListener('pointercancel', this.onUp);
    this.desktopMq.removeEventListener('change', this.sync);
    this.observer?.disconnect();
    this.observer = null;
    window.removeEventListener('resize', this.sync);
  }

  private initAutoScroll(): void {
    if (this.hasAutoScrolled) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || this.hasAutoScrolled) {
          return;
        }

        this.hasAutoScrolled = true;

        setTimeout(() => {
          this.scrollToInitialPosition();
        }, 300);

        this.observer?.disconnect();
        this.observer = null;
      },
      {
        threshold: 0.7,
      },
    );

    this.observer.observe(this.root);
  }

  private scrollToInitialPosition(): void {
    if (!this.overflow()) return;

    const maxScroll = this.root.scrollWidth - this.root.clientWidth;

    // Планшет / мобилка: сразу уводим горизонтальный скролл в конец.
    if (!this.desktopMq.matches) {
      this.animateScroll(Math.max(0, maxScroll), 1200);
      return;
    }

    const current = this.root.querySelector<HTMLElement>(
      '.clusters-detail-building-main__step--current',
    );

    if (!current) {
      this.animateScroll(Math.max(0, maxScroll), 1200);
      return;
    }

    const target = current.offsetLeft - this.root.clientWidth / 2 + current.clientWidth / 2;

    this.animateScroll(Math.max(0, Math.min(target, maxScroll)), 2000);
  }

  private animateScroll(target: number, duration = 1500): void {
    const start = this.root.scrollLeft;
    const distance = target - start;
    const startTime = performance.now();

    const step = (currentTime: number): void => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // easeInOutQuad
      const eased =
        progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      this.root.scrollLeft = start + distance * eased;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }

  private overflow = (): boolean => this.root.scrollWidth > this.root.clientWidth;

  private sync = (): void => {
    const on = this.overflow();
    this.root.classList.toggle('is-scrollable', on);
    if (!on) this.root.scrollLeft = 0;
  };

  private onDown = (e: PointerEvent): void => {
    if (!this.overflow() || e.button !== 0 || e.pointerType === 'touch') return;
    this.dragging = true;
    this.startX = e.clientX;
    this.startScroll = this.root.scrollLeft;
    this.root.setPointerCapture(e.pointerId);
    this.root.classList.add('is-dragging');
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.dragging) return;
    const dx = e.clientX - this.startX;
    if (Math.abs(dx) < 3) return;
    e.preventDefault();
    this.root.scrollLeft = this.startScroll - dx;
  };

  private onUp = (e: PointerEvent): void => {
    if (!this.dragging) return;
    this.dragging = false;
    this.root.classList.remove('is-dragging');
    this.root.releasePointerCapture(e.pointerId);
  };
}
